import { useState, useEffect, useCallback, useMemo } from 'react';
import { Text, Box, Loader, Center, ThemeIcon } from '@mantine/core';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Users, X } from 'react-feather';
import {
  cohortCoachesApi,
  type CohortCoachWithDetails,
  type CohortCoachAvailabilityStatus,
} from '../../../services/api/cohortCoaches';
import type { GroupWithMembers } from '../../../services/api/groups';
import type { SessionWithDetails } from '../../../services/api/sessions';
import type { Participant } from '../../../types';
import type { SelectionMode } from './groups/useCoachAssignment';
import { CoachCard, type CoachGroupAvailability } from './CoachCard';
import { getEffectiveConflictsForGroup, type GroupSessionsMap } from './groups/coachConflictUtils';
import { UnassignedParticipantCard } from './UnassignedParticipantCard';
import classes from './CoachesSidePanel.module.css';
import { isMedleyEmail } from '../../../utils/medleyEmail';

export type SidebarTab = 'coaches' | 'participants';

interface CoachesSidePanelProps {
  cohortId: string;
  groups: GroupWithMembers[];
  onCoachSelect: (coach: CohortCoachWithDetails) => void;
  onSendReminder: (coach: CohortCoachWithDetails) => void;
  selectedCoachId?: string;
  refreshTrigger?: number;
  onClearAll: () => void;
  groupSessionsMap?: Record<string, SessionWithDetails[]>;
  /** Current selection mode */
  selectionMode?: SelectionMode;
  /** Group IDs selected in group-first mode */
  groupFirstSelectedIds?: Set<string>;
  /** Check if a coach is available for all selected groups */
  isCoachAvailableForAllSelectedGroups?: (coach: CohortCoachWithDetails) => boolean;
  /** Handler to clear group-first selection */
  onClearGroupFirstSelection?: () => void;
  /** Tab state */
  activeTab: SidebarTab;
  onTabChange: (tab: SidebarTab) => void;
  /** Participant data for the unassigned tab */
  unassignedParticipants: Participant[];
  unassignedParticipantsLoading: boolean;
  unassignedCount: number;
  selectedParticipant: Participant | null;
  onParticipantSelect: (participant: Participant) => void;
  // MED-977: configured group-coaching session count for the per-group
  // availability widget (e.g. "4/4"); replaces hardcoded 6 default.
  configuredGroupCoachingSessionCount?: number | null;
  // MED-1161: peer-led cohorts have no coaches — hide the Coaches tab and its
  // availability content, leaving the Unassigned (participant) tab intact.
  isPeerLed?: boolean;
}

// Reuse filter/sort logic from CoachesPanel
const filterEligibleCoaches = (coaches: CohortCoachWithDetails[]): CohortCoachWithDetails[] => {
  return coaches.filter((coach) => {
    if (coach.availability_status === 'not_requested') return false;
    if (coach.availability_status === 'pending_response') return true;
    if (coach.availability_status === 'unavailable') return false;
    return true;
  });
};

const sortCoaches = (coaches: CohortCoachWithDetails[]): CohortCoachWithDetails[] => {
  return [...coaches].sort((a, b) => {
    const statusOrder: Record<CohortCoachAvailabilityStatus, number> = {
      available: 0,
      partially_available: 1,
      pending_response: 2,
      not_requested: 3,
      unavailable: 4,
    };
    const statusDiff = statusOrder[a.availability_status] - statusOrder[b.availability_status];
    if (statusDiff !== 0) return statusDiff;
    const availDiff = (b.availability_percent || 0) - (a.availability_percent || 0);
    if (availDiff !== 0) return availDiff;
    const satDiff = (b.satisfaction_score ?? 0) - (a.satisfaction_score ?? 0);
    if (satDiff !== 0) return satDiff;
    return (b.group_count || 0) - (a.group_count || 0);
  });
};

// MED-977: totalSessions is now the cohort's configured group-coaching count
// (0 when not configured); the previous hardcoded 6 caused the per-group
// availability widget to misreport coverage on non-6 cohorts.
// MED-978: per-group conflicts come from the shared
// `getEffectiveConflictsForGroup` helper so this widget agrees with the
// Coaches tab and the Groups assignment screen.
const calculateGroupAvailability = (
  coach: CohortCoachWithDetails,
  groups: GroupWithMembers[],
  totalSessions: number,
  groupSessionsMap: GroupSessionsMap,
): CoachGroupAvailability[] => {

  if (coach.availability_status === 'pending_response' || coach.availability_status === 'unavailable') {
    return groups.slice(0, 3).map((group) => ({
      groupId: group.id,
      groupName: group.name,
      availableSessions: coach.availability_status === 'unavailable' ? 0 : null,
      totalSessions,
      status: 'unknown' as const,
    }));
  }

  return groups.slice(0, 3).map((group) => {
    if (!group.scheduled_time_slot_id) {
      return { groupId: group.id, groupName: group.name, availableSessions: null, totalSessions, status: 'unknown' as const };
    }
    if (!coach.confirmed_time_slots || coach.confirmed_time_slots.length === 0) {
      return { groupId: group.id, groupName: group.name, availableSessions: 0, totalSessions, status: 'unknown' as const };
    }
    const hasMatchingSlot = coach.confirmed_time_slots.includes(group.scheduled_time_slot_id);
    if (!hasMatchingSlot) {
      return { groupId: group.id, groupName: group.name, availableSessions: 0, totalSessions, status: 'unknown' as const };
    }

    const effectiveConflicts = getEffectiveConflictsForGroup(
      coach,
      group,
      groupSessionsMap[group.id],
    );
    const availableSessions = Math.max(0, totalSessions - effectiveConflicts.length);
    const isPartial = effectiveConflicts.length > 0;

    return {
      groupId: group.id,
      groupName: group.name,
      availableSessions,
      totalSessions,
      status: isPartial ? 'partial' as const : 'full' as const,
      conflicts: isPartial ? effectiveConflicts : undefined,
    };
  });
};

const getMaxGroups = (coach: CohortCoachWithDetails): number | null => {
  return coach.max_groups ?? null;
};

const getIsFlexibleGroups = (coach: CohortCoachWithDetails): boolean => {
  return coach.is_flexible_groups ?? false;
};

// Draggable wrapper for coach cards
function DraggableCoachCard({
  coach,
  groups,
  onSelect,
  onSendReminder,
  isSelected,
  groupSessionsMap,
  disableDrag,
  configuredGroupCoachingSessionCount,
}: {
  coach: CohortCoachWithDetails;
  groups: GroupWithMembers[];
  onSelect: () => void;
  onSendReminder: () => void;
  isSelected: boolean;
  groupSessionsMap?: Record<string, SessionWithDetails[]>;
  disableDrag?: boolean;
  configuredGroupCoachingSessionCount?: number | null;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `coach-drag-${coach.id}`,
    data: { coach, type: 'coach' as const },
    disabled: disableDrag,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={disableDrag ? classes.coachCardClickOnly : classes.draggableCoachCard}
      {...(disableDrag ? {} : listeners)}
      {...(disableDrag ? {} : attributes)}
    >
      <CoachCard
        coach={coach}
        maxGroups={getMaxGroups(coach)}
        isFlexibleGroups={getIsFlexibleGroups(coach)}
        groupAvailability={calculateGroupAvailability(coach, groups, configuredGroupCoachingSessionCount ?? 0, groupSessionsMap ?? {})}
        extraGroupsCount={Math.max(0, groups.length - 3)}
        onSendReminder={onSendReminder}
        onSelect={onSelect}
        isSelected={isSelected}
        groupSessionsMap={groupSessionsMap}
      />
    </div>
  );
}

// Draggable wrapper for participant cards
function DraggableParticipantCard({
  participant,
  isSelected,
  onSelect,
}: {
  participant: Participant;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `participant-drag-${participant.user_id}`,
    data: { participant, type: 'participant' as const },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classes.draggableCoachCard}
      {...listeners}
      {...attributes}
    >
      <UnassignedParticipantCard
        participant={participant}
        isSelected={isSelected}
        onSelect={onSelect}
      />
    </div>
  );
}

export function CoachesSidePanel({
  cohortId,
  groups,
  onCoachSelect,
  onSendReminder,
  selectedCoachId,
  refreshTrigger,
  onClearAll,
  groupSessionsMap,
  selectionMode,
  groupFirstSelectedIds,
  isCoachAvailableForAllSelectedGroups,
  onClearGroupFirstSelection,
  activeTab,
  onTabChange,
  unassignedParticipants,
  unassignedParticipantsLoading,
  unassignedCount,
  selectedParticipant,
  onParticipantSelect,
  configuredGroupCoachingSessionCount,
  isPeerLed = false,
}: CoachesSidePanelProps) {
  const [coaches, setCoaches] = useState<CohortCoachWithDetails[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCoaches = useCallback(async () => {
    setLoading(true);
    try {
      const response = await cohortCoachesApi.list(cohortId, { limit: 50 });
      setCoaches(response.data);
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    fetchCoaches();
  }, [fetchCoaches, refreshTrigger]);

  const displayCoaches = useMemo(() => {
    const eligible = filterEligibleCoaches(coaches);
    return sortCoaches(eligible);
  }, [coaches]);

  const isGroupFirstFiltering = selectionMode === 'group-first' && (groupFirstSelectedIds?.size ?? 0) > 0;

  // Filter coaches when in group-first mode
  const filteredCoaches = useMemo(() => {
    if (!isGroupFirstFiltering || !isCoachAvailableForAllSelectedGroups) {
      return displayCoaches;
    }
    return displayCoaches.filter(isCoachAvailableForAllSelectedGroups);
  }, [displayCoaches, isGroupFirstFiltering, isCoachAvailableForAllSelectedGroups]);

  // Build filter indicator text
  const filterIndicatorText = useMemo(() => {
    if (!isGroupFirstFiltering || !groupFirstSelectedIds) return null;
    const selectedCount = groupFirstSelectedIds.size;
    const selectedGroups = groups.filter((g) => groupFirstSelectedIds.has(g.id));

    if (selectedCount === 1 && selectedGroups[0]) {
      return `Showing ${filteredCoaches.length} of ${displayCoaches.length} coaches for ${selectedGroups[0].name}`;
    }
    return `Showing ${filteredCoaches.length} of ${displayCoaches.length} coaches for ${selectedCount} selected groups`;
  }, [isGroupFirstFiltering, groupFirstSelectedIds, groups, filteredCoaches.length, displayCoaches.length]);

  const assignedCount = groups.filter((g) => g.coach_id).length;

  // MED-1161: peer-led cohorts have no Coaches tab; always render the
  // participants (Unassigned) content regardless of the persisted tab state.
  const isCoachesTab = !isPeerLed && activeTab === 'coaches';
  const isLoading = isCoachesTab ? loading : unassignedParticipantsLoading;

  return (
    <Box className={classes.panel}>
      {/* Tab Header */}
      <div className={classes.header}>
        <div className={classes.tabRow}>
          {/* MED-1161: Coaches tab hidden for peer-led cohorts. */}
          {!isPeerLed && (
            <button
              type="button"
              className={`${classes.tab} ${isCoachesTab ? classes.tabActive : ''}`}
              onClick={() => onTabChange('coaches')}
            >
              Coaches
              <span className={classes.tabCount}>{filteredCoaches.length}</span>
            </button>
          )}
          <button
            type="button"
            className={`${classes.tab} ${!isCoachesTab ? classes.tabActive : ''}`}
            onClick={() => onTabChange('participants')}
          >
            Unassigned
            <span className={classes.tabCount}>{unassignedCount}</span>
          </button>
        </div>
        <div className={classes.subtitle}>
          {isCoachesTab
            ? (isGroupFirstFiltering ? 'Click a coach to assign' : 'Drag to assign to groups')
            : 'Click to filter \u00B7 Drag to assign'}
        </div>
      </div>

      {/* Filter Indicator (coaches tab only) */}
      {isCoachesTab && filterIndicatorText && (
        <div className={classes.filterIndicator}>
          <Text size="xs" c="blue" fw={500} style={{ flex: 1 }}>
            {filterIndicatorText}
          </Text>
          <button
            type="button"
            className={classes.filterClearButton}
            onClick={onClearGroupFirstSelection}
            title="Clear group selection"
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <Center py="xl" style={{ flex: 1 }}>
          <Loader size="sm" />
        </Center>
      )}

      {/* Coaches Tab Content */}
      {isCoachesTab && !loading && (
        <>
          {displayCoaches.length === 0 ? (
            <div className={classes.emptyState}>
              <ThemeIcon size={40} radius={40} color="gray" variant="light" mx="auto" mb="sm">
                <Users size={20} />
              </ThemeIcon>
              <Text size="sm" c="dimmed">
                No coaches with availability
              </Text>
            </div>
          ) : (
            <div className={classes.coachList}>
              {filteredCoaches.map((coach) => (
                <DraggableCoachCard
                  key={coach.id}
                  coach={coach}
                  groups={groups}
                  onSelect={() => onCoachSelect(coach)}
                  onSendReminder={() => onSendReminder(coach)}
                  isSelected={selectedCoachId === coach.id}
                  groupSessionsMap={groupSessionsMap}
                  // MED-1355: a coach with no Medley email cannot lead a group,
                  // so the card cannot be dragged onto one.
                  disableDrag={isGroupFirstFiltering || !isMedleyEmail(coach.email)}
                  configuredGroupCoachingSessionCount={configuredGroupCoachingSessionCount}
                />
              ))}
              {isGroupFirstFiltering && filteredCoaches.length === 0 && (
                <div className={classes.emptyState}>
                  <Text size="sm" c="dimmed">
                    No coaches available for the selected group(s)
                  </Text>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Participants Tab Content */}
      {!isCoachesTab && !unassignedParticipantsLoading && (
        <>
          {unassignedParticipants.length === 0 ? (
            <div className={classes.emptyState}>
              <ThemeIcon size={40} radius={40} color="gray" variant="light" mx="auto" mb="sm">
                <Users size={20} />
              </ThemeIcon>
              <Text size="sm" c="dimmed">
                All participants are assigned
              </Text>
            </div>
          ) : (
            <div className={classes.coachList}>
              {unassignedParticipants.map((participant) => (
                <DraggableParticipantCard
                  key={participant.user_id}
                  participant={participant}
                  isSelected={selectedParticipant?.user_id === participant.user_id}
                  onSelect={() => onParticipantSelect(participant)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Footer (coaches tab only) */}
      {isCoachesTab && assignedCount > 0 && (
        <div className={classes.footer}>
          <button
            type="button"
            className={classes.clearButton}
            onClick={onClearAll}
          >
            Clear all assignments ({assignedCount})
          </button>
        </div>
      )}
    </Box>
  );
}
